import { defineStore } from "pinia";
import axios from "@/api/axios";
import {
  FileMetadata,
  FileData,
  UploadFilesResponse,
  FilesMetadataResponse,
  FilesDataResponse,
  FilesDataQuery,
  ApproveFilesRequest,
  FederalDistrictData,
  FederalDistrictDataQuery,
  Month,
} from "@/types/files";
import { useEventLogStore } from "@/stores/event-log";
import {useStore} from "@/stores/index";
import {AxiosProgressEvent} from "axios";
import createFileDownloadLink from "@/composables/createFileDownloadLink";
import type {DistrictKey} from "@/composables/functions/getDistrictName";
import type {RegionKey} from "@/composables/functions/getRegionName";

export const useFilesStore = defineStore("FilesStore", {
  state: () => ({
    filesMetadata: [] as FileMetadata[],
    filesMetadataPagination: null as FilesMetadataResponse | null,
    currentFileData: null as FileData | null,
    currentFileMetadata: null as FileMetadata | null,
    currentFileDistrictsData: null as FederalDistrictData | null,
    filesData: null as FilesDataResponse | null,

    isLoadingFilesData: false as boolean,

    selectedMonth: "july" as Month,
    selectedYear: new Date().getFullYear() as number,
    selectedDistrictKey: null as DistrictKey | null,
    selectedRegionKey: null as RegionKey | null
  }),

  getters: {
    getFilesMetadata: (state) => state.filesMetadata,
    getCurrentFileData: (state) => state.currentFileData,
    getCurrentFileMetadata: (state) => state.currentFileMetadata,
    getFilesMetadataPagination: (state) => state.filesMetadataPagination,
  },

  actions: {
    /**
     * POST /api/files/upload
     * Загрузка нескольких файлов для проверки
     */
    async uploadFiles(
        files: File[],
        onUploadProgress?: (_progressEvent: AxiosProgressEvent) => void
    ): Promise<UploadFilesResponse> {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await axios.post<UploadFilesResponse>(
          "/oracul/api/files/upload",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress,
          }
      );


      // Обновляем лог событий после успешной загрузки
      const eventLogStore = useEventLogStore();
      await eventLogStore.getEventsLogQuery();

      return response.data;
    },

    /**
     * PUT /api/files/data/{fileId}
     * Обновить данные файла по идентификатору
     */
    async updateFileData(fileId: string, data: FileData): Promise<FileData> {
      const response = await axios.put<FileData>(
          `/oracul/api/files/data/${fileId}`,
          data
      );


      this.currentFileData = response.data;
      return response.data;
    },


    /**
     * GET /api/files/data/{fileId}
     * Получить данные файла по идентификатору
     * */
    async getFileData(fileId: string): Promise<FileData> {
      const response = await axios.get<FileData>(
          `/oracul/api/files/data/${fileId}`
      );


      this.currentFileData = response.data;
      return response.data;
    },


    /**
     * GET /api/files/metadata
     * Получить метаданные всех файлов, которые загрузил пользователь
     * Поддерживает пагинацию через параметры page и limit
     */
    async getAllFilesMetadata(page?: number, limit?: number): Promise<FilesMetadataResponse> {
      const params: { page?: number; limit?: number } = {};

      if (page !== undefined) {
        params.page = page;
      }

      if (limit !== undefined) {
        params.limit = limit;
      }

      const response = await axios.get<FilesMetadataResponse>(
          "/oracul/api/files/metadata",
          { params }
      );


      this.filesMetadata = response.data.results;
      this.filesMetadataPagination = response.data;
      return response.data;
    },

    /**
     * GET /api/files/metadata/{fileId}
     * Получить метаданные файла по идентификатору
     */
    async getFileMetadata(fileId: string): Promise<FileMetadata> {
      const response = await axios.get<FileMetadata>(
          `/oracul/api/files/metadata/${fileId}`
      );

      this.currentFileMetadata = response.data;
      return response.data;
    },

    /**
     * GET /api/files/export/{fileId}
     * Скачивание файла
     */
    async exportFile(fileId: string): Promise<void> {
      const response = await axios.get(
          `/oracul/api/files/export/${fileId}`,
          {
            responseType: "blob",
          }
      );

      createFileDownloadLink(response, `${fileId}`);
    },

    /**
     * GET /api/files/data
     * Получить данные файлов по региону и периоду
     * Поддерживает: пагинацию, фильтрацию и сортировку
     */
    async getFilesData(params?: FilesDataQuery): Promise<FilesDataResponse> {

      this.isLoadingFilesData = true;

      // Сброс данных.
      // this.filesData = null;

      const response = await axios.get<FilesDataResponse>(
          "/oracul/api/files/data?limit=99999",
          { params }
      );

      this.filesData = response.data;

      if (params && params.month) {
        this.selectedMonth = params.month;
      }

      this.isLoadingFilesData = false;

      return response.data;
    },

    /**
     * POST /api/files/approve
     * Подтверждение нескольких файлов
     * Принимает список fileIds, возвращает void при успехе или ErrorResponse при 404
     */
    async approveFiles(fileIds: ApproveFilesRequest): Promise<void> {
      const store = useStore();

      try {
        await axios.post<void>(
            "/oracul/api/files/approve",
            fileIds
        );

        // Обновляем лог событий после успешного подтверждения
        const eventLogStore = useEventLogStore();
        await eventLogStore.getEventsLogQuery();

        // Обновляем список файлов
        await this.getFilesData();

        // Добавляем тост об успехе
        store.showSuccess("Данные загружены успешно", "Выберите показатели и период для отображения");
      } catch (error) {
        // Добавляем тост об ошибке
        store.showError("Ошибка подтверждения файлов", "Не удалось подтвердить выбранные файлы");
        throw error;
      }
    },

    /**
     * GET /api/files/metric-by-federal-districts
     * Получить данные метрики по федеральным округам, которые загрузил пользователь
     */
    async getFederalDistrictData(query: FederalDistrictDataQuery) {
      const response = await axios.get(
          "/oracul/api/files/metric-by-federal-districts",
          { params: query }
      );

      this.currentFileDistrictsData = response.data;

      return response.data;
    },
  },
});